import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { TableKit } from '@tiptap/extension-table';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import axios from 'axios';
import { appendApplicationId, getApiBaseUrl } from '../../utils/apiConfig';
import { getStorageValues } from '../CommonFunctions';
import { buildImageUrlFromUpload } from '../../pages/ec-admin/product/utils/productHelpers';
import TipTapToolbar from './TipTapToolbar';
import TipTapBubbleMenu from './TipTapBubbleMenu';
import './tiptap-editor.css';

const EMPTY_HTML = '<p></p>';

const normalizeOutput = (html) => {
  const raw = String(html || '').trim();
  if (!raw || raw === EMPTY_HTML || raw === '<p><br></p>') return '';
  return raw;
};

const safeEditorHtml = (ed) => {
  if (!ed || ed.isDestroyed) return '';
  try {
    return ed.getHTML();
  } catch {
    return '';
  }
};

const TIPTAP_EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
  Underline,
  TextStyle,
  Color.configure({ types: ['textStyle'] }),
  Highlight.configure({ multicolor: true }),
  Link.configure({ openOnClick: false }),
  Image,
  TableKit.configure({ table: { resizable: true } }),
];

function TipTapEditor({
  value = '',
  onChange,
  readOnly = false,
  placeholder = '',
  productId = null,
  className = '',
}) {
  const skipValueSyncRef = useRef(false);
  const lastExternalValueRef = useRef(normalizeOutput(value));

  const editor = useEditor({
    extensions: TIPTAP_EXTENSIONS,
    content: value || EMPTY_HTML,
    editable: !readOnly,
    editorProps: {
      attributes: {
        'data-placeholder': placeholder || '',
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = normalizeOutput(safeEditorHtml(ed));
      skipValueSyncRef.current = true;
      lastExternalValueRef.current = html;
      onChange?.(html);
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setOptions({
      editorProps: {
        attributes: {
          'data-placeholder': placeholder || '',
        },
      },
    });
  }, [editor, placeholder]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const next = normalizeOutput(value);
    if (skipValueSyncRef.current) {
      skipValueSyncRef.current = false;
      return;
    }
    if (next === lastExternalValueRef.current) return;
    const current = normalizeOutput(safeEditorHtml(editor));
    if (next === current) {
      lastExternalValueRef.current = next;
      return;
    }
    editor.commands.setContent(value || EMPTY_HTML, { emitUpdate: false });
    lastExternalValueRef.current = next;
  }, [editor, value]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  const uploadImage = async (file) => {
    if (!file || !productId) return null;
    const formDataToSend = new FormData();
    formDataToSend.append('entityId', productId);
    formDataToSend.append('entityType', 'ec_product');
    formDataToSend.append('fileCategory', 'product-images');
    formDataToSend.append('UserId', getStorageValues('userId'));
    formDataToSend.append('files', file, file.name);
    appendApplicationId(formDataToSend);

    const uploadRes = await axios.post(`${getApiBaseUrl()}/file/upload`, formDataToSend, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const uploaded = uploadRes.data?.data?.[0];
    if (!uploaded?.FilePath) return null;
    return buildImageUrlFromUpload(uploaded.FilePath);
  };

  const handleImageClick = () => {
    if (!editor || readOnly) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadImage(file);
        if (url) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      } catch (err) {
        console.error(err);
      }
    };
    input.click();
  };

  if (!editor) {
    return <div className={`sk-tiptap-editor sk-tiptap-loading ${className}`}>Loading editor…</div>;
  }

  return (
    <div className={`sk-tiptap-editor border rounded ${className}`}>
      {!readOnly ? (
        <>
          <TipTapToolbar
            editor={editor}
            onImageClick={handleImageClick}
            imageUploadDisabled={!productId}
          />
          <TipTapBubbleMenu editor={editor} />
        </>
      ) : null}
      <EditorContent editor={editor} className="sk-tiptap-content p-2" data-placeholder={placeholder} />
    </div>
  );
}

export default TipTapEditor;
